import type { ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import type { EffectLine, Requirement, Tone } from '../types';
import styles from './dialogs.module.css';

/** Building blocks of the stage dialogs (Deal-Pipeline 17–22, A2 „Stage-Wechsel-Dialog-Chrome"). */

const toneText: Record<Tone, string | undefined> = {
  success: styles.toneSuccess,
  danger: styles.toneDanger,
  warning: styles.toneWarning,
  info: styles.toneInfo,
  neutral: styles.toneNeutral,
  accent: styles.toneNeutral,
};

const toneBg: Record<Tone, string | undefined> = {
  success: styles.bgSuccess,
  danger: styles.bgDanger,
  warning: styles.bgWarning,
  info: styles.bgInfo,
  neutral: styles.bgNeutral,
  accent: styles.bgNeutral,
};

export interface FlowChipProps {
  from: string;
  to: string;
  arrow: string;
  tone: Tone;
  /** „setzt: Berater" · „harte Sperre" · „Rückwärtsbewegung" */
  meta: string;
}

/** The „Von → Nach · setzt: <Rolle>" row. */
export function FlowChip({ from, to, arrow, tone, meta }: FlowChipProps) {
  return (
    <div className={styles.flow}>
      <span className={styles.flowFrom}>{from}</span>
      <span className={clsx(styles.flowArrow, toneText[tone])} aria-hidden="true">
        {arrow}
      </span>
      <span className={clsx(styles.flowTo, toneBg[tone], toneText[tone])}>{to}</span>
      <span className={styles.flowMeta}>{meta}</span>
    </div>
  );
}

function markOf(state: 'met' | 'missing' | 'warning' | 'info'): string {
  return state === 'met' ? '✓' : state === 'missing' ? '✕' : '!';
}

export interface RequirementRowProps {
  state: 'met' | 'missing' | 'warning' | 'info';
  label: string;
  detail?: string | null | undefined;
  owner: string;
}

/** One ✓/✕/! row with label, detail and the owner label on the right. */
export function RequirementRow({ state, label, detail, owner }: RequirementRowProps) {
  return (
    <div
      className={clsx(
        styles.req,
        state === 'missing' && styles.reqMissing,
        state === 'warning' && styles.reqWarning,
        state === 'info' && styles.reqInfo,
      )}
    >
      <span className={styles.reqMark} aria-hidden="true">
        {markOf(state)}
      </span>
      <div className={styles.reqText}>
        <div className={styles.reqLabel}>{label}</div>
        {detail ? <div className={styles.reqDetail}>{detail}</div> : null}
      </div>
      <span className={styles.reqOwner}>{owner}</span>
    </div>
  );
}

/** „Voraussetzungen" of a forward stage change. */
export function RequirementList({ title, items }: { title?: string | undefined; items: Requirement[] }) {
  const sr = (item: Requirement) =>
    item.state === 'met' ? 'erfüllt' : item.state === 'missing' ? 'fehlt' : 'Warnung';
  return (
    <div>
      {title ? <div className={styles.sectionLabel}>{title}</div> : null}
      <ul className={clsx(title ? styles.list : styles.listTight)} aria-label={title ?? 'Voraussetzungen'}>
        {items.map((item) => (
          <li key={item.key} aria-label={`${item.label} · ${sr(item)}`}>
            <RequirementRow
              state={item.state}
              label={item.label}
              detail={item.detail}
              owner={item.ownerLabel}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** „Was der Schritt auslöst" of a backward or late stage change (Deal-Pipeline 20). */
export function EffectList({ title, items }: { title: string; items: EffectLine[] }) {
  return (
    <div>
      <div className={styles.sectionLabel}>{title}</div>
      <ul className={styles.list} aria-label={title}>
        {items.map((item) => (
          <li key={item.label}>
            <RequirementRow
              state={item.ownerLabel === 'System' ? 'met' : 'info'}
              label={item.label}
              detail={item.detail || null}
              owner={item.ownerLabel}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface FieldBlockProps {
  label: string;
  /** „Pflichtfeld" · „Pflicht bei Warnung" · „wird mit angelegt" */
  note?: string | undefined;
  error?: string | null | undefined;
  htmlFor?: string | undefined;
  children: ReactNode;
}

/** Label row („Nächster Schritt" … „Pflichtfeld") with the field below. */
export function FieldBlock({ label, note, error, htmlFor, children }: FieldBlockProps) {
  return (
    <div>
      <div className={styles.fieldHead}>
        {htmlFor ? (
          <label htmlFor={htmlFor} className={styles.fieldLabel}>
            {label}
          </label>
        ) : (
          <span className={styles.fieldLabel}>{label}</span>
        )}
        {note ? <span className={styles.fieldNote}>{note}</span> : null}
      </div>
      <div className={styles.fieldBody}>{children}</div>
      {error ? (
        <div className={styles.fieldError} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/** The info line of Deal-Pipeline 21 („Kontakt geht auf Follow Up · WE 2.01 wird frei"). */
export function InfoLine({ children }: { children: ReactNode }) {
  return (
    <div className={styles.infoLine} role="status">
      <span className={styles.infoDot} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
